import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import {
  getAllClassroom,
  getAllSubject,
  getClassByLevel,
} from "../../api/pedagogicElement";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { getAllEvaluationPeriodByLevel } from "../../api/generalParam";
import { useEffect, useState } from "react";
import { Select } from "antd";
import { getAllTeachers } from "../../api/teachers";
import { createExamPlanning } from "../../api/planningExam";

//Typage des props parents
type Props = {
  show: boolean;
  classSelected: any;
  periodSelected: any;
  levelSelected: any;
  onClose: () => void;
  onSaved?: () => void;
};

type ConsigneItem = { id: string; label: string };

const buildDefaultConsignes = (): ConsigneItem[] => [
  { id: crypto.randomUUID(), label: "Carte étudiante obligatoire" },
  { id: crypto.randomUUID(), label: "Téléphones interdits pendant l'examen" },
  { id: crypto.randomUUID(), label: "Calculatrice scientifique autorisée" },
  { id: crypto.randomUUID(), label: "Aucun document personnel autorisé" },
];

const CreateExamModal = ({
  show,
  onClose,
  onSaved,
  classSelected,
  periodSelected,
  levelSelected,
}: Props) => {
  const [availableConsignes, setAvailableConsignes] = useState<ConsigneItem[]>(
    buildDefaultConsignes,
  );

  //Initialisation de l'année academmique actuelle
  const { selectedYear, setSelectedYear } = useAcademicYear();

  const [allClass, setAllClass] = useState<any[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<any[]>([]);
  const [allClassrooms, setAllClassrooms] = useState<any[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<any>();
  const [selectedClassroom, setSelectedClassroom] = useState<any>();
  const [allSubjects, setAllSubjects] = useState<any[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<any>();
  const [allEvaluationPeriod, setAllEvaluationPeriod] = useState<any[]>([]);
  const [selectedEvaluationPeriod, setSelectedEvaluationPeriod] =
    useState<any>(null);

  const [selectedConsigneIds, setSelectedConsigneIds] = useState<string[]>([]);
  const [newConsigne, setNewConsigne] = useState("");
  const [editingConsigneId, setEditingConsigneId] = useState<string | null>(
    null,
  );
  const [editingConsigneValue, setEditingConsigneValue] = useState("");
  const [selectedStartHour, setSelectedStartHour] = useState<string>("");
  const [selectedEndHour, setSelectedEndHour] = useState<string>("");

  //Schéma de notre formulaire
  const createSchema = z.object({
    id_evaluationPeriod: z
      .string()
      .nonempty("Le libellé du niveau d'étude est requis"),
    classes: z.array(z.string()).min(1, "La classe est requise"),
    id_subject: z.string().nonempty("La matière est requise"),
    id_classroom: z.string().nonempty("La salle d'examen est requise"),
    id_teacherProfil: z
      .string({
        required_error: "L'enseignant est requis",
      })
      .nonempty("L'enseignant est requis"),
    date_exam: z.string().nonempty("La date de l'examen est requise"),
    sessionStart: z
      .string({
        required_error: "L'heure de début de l'examen est requise",
      })
      .nonempty("L'heure de début de l'examen est requise"),
    sessionEnd: z
      .string({
        required_error: "L'heure de fin de l'examen est requise",
      })
      .nonempty("L'heure de fin de l'examen est requise"),
    practical: z.boolean(),
    type: z.enum(["Devoir", "Examen"], {
      required_error: "Le type d'évaluation est requis",
    }),
    consignes: z.array(z.string()),
  });

  type FormFields = z.infer<typeof createSchema>;

  const defaultFormValues: Partial<FormFields> = {
    practical: false,
    type: "Examen",
    classes: [],
    consignes: [],
    id_evaluationPeriod: "",
    id_subject: "",
    id_classroom: "",
    id_teacherProfil: "",
    date_exam: "",
    sessionStart: "",
    sessionEnd: "",
  };

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
    defaultValues: defaultFormValues,
  });

  const examType = watch("type");

  const resetExamForm = () => {
    reset(defaultFormValues);
    setSelectedClasses([]);
    setSelectedTeacher(undefined);
    setSelectedClassroom(undefined);
    setSelectedSubject(undefined);
    setSelectedEvaluationPeriod(null);
    setSelectedConsigneIds([]);
    setNewConsigne("");
    setEditingConsigneId(null);
    setEditingConsigneValue("");
    setSelectedStartHour("");
    setSelectedEndHour("");
    setAvailableConsignes(buildDefaultConsignes());
  };

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour création vers le backend
      await createExamPlanning(data);

      resetExamForm(); // On réinitialise aussi les Select et les consignes
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Examen planifié !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const fetchAllClassByLevel = async () => {
    try {
      if (!selectedYear && !levelSelected) return;
      const response = await getClassByLevel(
        selectedYear?.id,
        levelSelected?.id,
        buildPermissionContext(Resource.EDTE, Action.CREATE),
      );
      const classes =
        (response.data || []).slice().sort((a: any, b: any) => {
          const levelCompare = a.Level.label.localeCompare(
            b.Level.label,
            "fr",
            { numeric: true },
          );
          if (levelCompare !== 0) return levelCompare;
          return a.label.localeCompare(b.label, "fr", { numeric: true });
        }) || [];

      setAllClass(classes);

      if (classes.length > 0) {
        const preferredClass = classSelected
          ? classes.find((c: any) => c.id === classSelected?.id)
          : null;
        setSelectedClasses(preferredClass ? [preferredClass] : [classes[0]]);
      } else {
        setSelectedClasses([]);
      }
    } catch (error: any) {
      // console.log("Erreur lors de la récupération des classes:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const fetchPeriods = async (levelId?: string) => {
    if (!levelId) {
      setAllEvaluationPeriod([]);
      setSelectedEvaluationPeriod(null);
      return;
    }
    try {
      const response = await getAllEvaluationPeriodByLevel(
        levelId,
        buildPermissionContext(Resource.EDTE, Action.CREATE),
      );
      const periods = response.data || [];

      setAllEvaluationPeriod(periods);

      // Pré-sélectionne la période choisie dans la liste si disponible, sinon la première.
      if ((periods || []).length > 0) {
        const preferredPeriod = periodSelected
          ? periods.find((p: any) => p.id === periodSelected?.id)
          : null;
        setSelectedEvaluationPeriod(preferredPeriod || periods[0]);
      } else {
        setSelectedEvaluationPeriod(null);
      }
    } catch (error: any) {
      // console.log("erreur de chargement des périodes : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
      // Option: reset les periodes en cas d'erreur
      setAllEvaluationPeriod([]);
    }
  };

  const fetchClassroom = async () => {
    // On attend que selectedYear soit défini
    if (!selectedYear?.id) return;
    try {
      const response = await getAllClassroom(
        selectedYear?.id,
        buildPermissionContext(Resource.EDTE, Action.CREATE),
      );
      const classrooms = response.data || [];

      setAllClassrooms(classrooms);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les salles en cas d'erreur
      setAllClassrooms([]);
    }
  };

  const fetchSubject = async () => {
    // On attend que selectedYear soit défini
    if (!selectedYear?.id) return;

    try {
      const subjects = await getAllSubject(
        selectedYear?.id,
        buildPermissionContext(Resource.EDTE, Action.CREATE),
      );
      setAllSubjects(subjects.data);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les matières en cas d'erreur
      setAllSubjects([]);
    }
  };

  // Fonction pour récupérer les enseignants
  const fetchTeachers = async () => {
    try {
      const teachersData = await getAllTeachers(
        buildPermissionContext(Resource.EDTE, Action.CREATE),
      );
      const formattedTeachers = teachersData.data.map((teacher: any) => ({
        id: teacher.id,
        UserProfil: {
          firstName: teacher.firstname,
          lastName: teacher.lastname,
        },
      }));
      setTeachers(formattedTeachers);
    } catch (error: any) {
      // console.log("erreur récupération des enseignants ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const timeOptions = Array.from({ length: 32 }, (_, i) => {
    const hour = 7 + Math.floor(i / 2); // 07:00 -> 22:30
    const min = i % 2 === 0 ? "00" : "30";
    const value = `${String(hour).padStart(2, "0")}:${min}`;
    return value;
  });

  const toMinutes = (time: string) => {
    const [hour, minute] = time.split(":").map(Number);
    return hour * 60 + minute;
  };

  const endTimeOptions = selectedStartHour
    ? timeOptions.filter(
        (time) => toMinutes(time) > toMinutes(selectedStartHour),
      )
    : timeOptions;

  useEffect(() => {
    if (!show) return;
    fetchAllClassByLevel();
    fetchClassroom();
    fetchSubject();
    fetchTeachers();
  }, [selectedYear?.id, levelSelected?.id, show, classSelected?.id]);

  useEffect(() => {
    if (!show) return;
    fetchPeriods(selectedClasses[0]?.Level?.id);
  }, [selectedClasses[0]?.Level?.id, periodSelected?.id, show]);

  useEffect(() => {
    setValue("classes", selectedClasses?.map((cls) => cls.id) || []);
  }, [selectedClasses, setValue]);

  useEffect(() => {
    setValue("id_evaluationPeriod", selectedEvaluationPeriod?.id || "");
  }, [selectedEvaluationPeriod?.id, setValue]);

  useEffect(() => {
    setValue("id_subject", selectedSubject?.id || "");
  }, [selectedSubject?.id, setValue]);

  useEffect(() => {
    setValue("id_classroom", selectedClassroom?.id || "");
  }, [selectedClassroom?.id, setValue]);

  const toggleConsigne = (id: string) => {
    setSelectedConsigneIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const deleteConsigne = (id: string) => {
    setAvailableConsignes((prev) => prev.filter((i) => i.id !== id));
    setSelectedConsigneIds((prev) => prev.filter((x) => x !== id));
  };

  const startEditConsigne = (id: string) => {
    const current = availableConsignes.find((i) => i.id === id);
    if (!current) return;
    setEditingConsigneId(id);
    setEditingConsigneValue(current.label);
  };

  const saveEditedConsigne = () => {
    if (!editingConsigneId) return;
    const next = editingConsigneValue.trim();
    if (!next) return;
    setAvailableConsignes((prev) =>
      prev.map((i) => (i.id === editingConsigneId ? { ...i, label: next } : i)),
    );
    setEditingConsigneId(null);
    setEditingConsigneValue("");
  };

  const cancelEditConsigne = () => {
    setEditingConsigneId(null);
    setEditingConsigneValue("");
  };

  const addConsigne = () => {
    const value = newConsigne.trim();
    if (!value) return;
    const exists = availableConsignes.some(
      (i) => i.label.toLowerCase() === value.toLowerCase(),
    );
    if (exists) return;
    const id = crypto.randomUUID();
    setAvailableConsignes((prev) => [...prev, { id, label: value }]);
    setSelectedConsigneIds((prev) => [...prev, id]);
    setNewConsigne("");
  };

  useEffect(() => {
    const nextConsignes = availableConsignes
      .filter((i) => selectedConsigneIds.includes(i.id))
      .map((i) => i.label);

    setValue("consignes", nextConsignes, { shouldValidate: true });
  }, [availableConsignes, selectedConsigneIds, setValue]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Programmer un Examen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Message d'erreur serveur */}
          {errors.root && (
            <div className="alert alert-danger rounded-pill alert-dismissible fade show">
              {errors.root.message}
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => clearErrors("root")}
              >
                <i className="fas fa-xmark" />
              </button>
            </div>
          )}
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6 ">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="card shadow-sm border-dark rounded">
                  <div className="card-header bg-light d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">Informations de l'examen</h5>
                  </div>
                  <div className="card-body pb-0 pt-1 row">
                    <div className="col-xxl-12 col-xl-12 col-md-12">
                      <div className="mb-2">
                        <label
                          className="form-label mb-1"
                          htmlFor="validationServer01"
                        >
                          Classes
                        </label>
                        <Select
                          mode="multiple"
                          showSearch
                          allowClear
                          size="large"
                          optionFilterProp="label"
                          removeIcon={<span style={{ fontSize: 12 }}>×</span>}
                          style={{ width: "100%" }}
                          getPopupContainer={(trigger) =>
                            trigger.parentElement as HTMLElement
                          }
                          dropdownStyle={{ zIndex: 2000 }}
                          placeholder="Sélectionner les classes"
                          value={
                            Array.isArray(selectedClasses)
                              ? selectedClasses.map((cls) => cls.id)
                              : []
                          }
                          onChange={(ids) => {
                            const selected = allClass.filter((cls) =>
                              ids.includes(cls.id),
                            );
                            setSelectedClasses(selected);
                            setValue("classes", ids || [], {
                              shouldValidate: true,
                            });
                          }}
                          onClear={() => setSelectedClasses([])}
                        >
                          {allClass.map((cls: any) => (
                            <Select.Option
                              key={cls.id}
                              value={cls.id}
                              label={`${cls.Level?.abbreviation ?? ""} - ${cls.label ?? ""}`}
                            >
                              {cls.Level?.abbreviation} - {cls?.label}
                            </Select.Option>
                          ))}
                        </Select>
                        {errors.classes && (
                          <div className="invalid-feedback d-block">
                            {errors.classes.message}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-xxl-6 col-xl-6 col-md-6">
                      <div className="mb-2">
                        <label
                          className="form-label mb-1"
                          htmlFor="validationServer01"
                        >
                          Période d'évaluation
                        </label>
                        <Select
                          showSearch
                          size="large"
                          optionFilterProp="label"
                          style={{ width: "100%" }}
                          getPopupContainer={(trigger) =>
                            trigger.parentElement as HTMLElement
                          }
                          dropdownStyle={{ zIndex: 2000 }}
                          placeholder="Sélectionner une période d'évaluation"
                          value={selectedEvaluationPeriod?.id || undefined}
                          onChange={(periodId) => {
                            const nextPeriod = allEvaluationPeriod.find(
                              (p: any) => p.id === periodId,
                            );
                            setSelectedEvaluationPeriod(nextPeriod);
                            setValue("id_evaluationPeriod", periodId || "", {
                              shouldValidate: true,
                            });
                          }}
                        >
                          {allEvaluationPeriod.map((period: any) => (
                            <Select.Option
                              key={period.id}
                              value={period.id}
                              label={`${period.label ?? ""} - ${selectedYear?.label ?? ""}`}
                            >
                              {period.label} - {selectedYear?.label}
                            </Select.Option>
                          ))}
                        </Select>
                        {errors.id_evaluationPeriod && (
                          <div className="invalid-feedback d-block">
                            {errors.id_evaluationPeriod.message}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-xxl-6 col-xl-6 col-md-6">
                      <div className="mb-2">
                        <label
                          className="form-label mb-1"
                          htmlFor="validationServer01"
                        >
                          Matière
                        </label>
                        <Select
                          showSearch
                          size="large"
                          optionFilterProp="label"
                          style={{ width: "100%" }}
                          getPopupContainer={(trigger) =>
                            trigger.parentElement as HTMLElement
                          }
                          dropdownStyle={{ zIndex: 2000 }}
                          placeholder="Sélectionner une matière"
                          value={selectedSubject?.id || undefined}
                          onChange={(subjectId) => {
                            const nextSubject = allSubjects.find(
                              (s: any) => s.id === subjectId,
                            );
                            setSelectedSubject(nextSubject);
                            setValue("id_subject", subjectId || "", {
                              shouldValidate: true,
                            });
                          }}
                        >
                          {allSubjects.map((subject: any) => (
                            <Select.Option
                              key={subject.id}
                              value={subject.id}
                              label={subject.label ?? ""}
                            >
                              {subject.label}
                            </Select.Option>
                          ))}
                        </Select>
                        {errors.id_subject && (
                          <div className="invalid-feedback d-block">
                            {errors.id_subject.message}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-xxl-6 col-xl-6 col-md-6">
                      <div className="mb-2">
                        <label
                          className="form-label mb-1"
                          htmlFor="validationServer01"
                        >
                          Professeur
                        </label>
                        <Select
                          showSearch
                          size="large"
                          optionFilterProp="label"
                          style={{ width: "100%" }}
                          getPopupContainer={(trigger) =>
                            trigger.parentElement as HTMLElement
                          }
                          dropdownStyle={{ zIndex: 2000 }}
                          placeholder="Sélectionner un professeur"
                          value={selectedTeacher?.id || undefined}
                          onChange={(teacherId) => {
                            const nextTeacher = teachers.find(
                              (t: any) => t.id === teacherId,
                            );
                            setSelectedTeacher(nextTeacher);
                            setValue("id_teacherProfil", teacherId || "", {
                              shouldValidate: true,
                            });
                          }}
                        >
                          {teachers.map((teacher: any) => (
                            <Select.Option
                              key={teacher.id}
                              value={teacher.id}
                              label={
                                teacher.UserProfil?.firstName &&
                                teacher.UserProfil?.lastName
                                  ? `${teacher.UserProfil.firstName} ${teacher.UserProfil.lastName}`
                                  : "Enseignant sans nom"
                              }
                            >
                              {teacher.UserProfil.firstName}{" "}
                              {teacher.UserProfil.lastName}
                            </Select.Option>
                          ))}
                        </Select>
                        {errors.id_teacherProfil && (
                          <div className="invalid-feedback d-block">
                            {errors.id_teacherProfil.message}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-xxl-6 col-xl-6 col-md-6">
                      <div className="mb-2">
                        <label
                          className="form-label mb-1"
                          htmlFor="validationServer01"
                        >
                          Salle
                        </label>
                        <Select
                          showSearch
                          size="large"
                          optionFilterProp="label"
                          style={{ width: "100%" }}
                          getPopupContainer={(trigger) =>
                            trigger.parentElement as HTMLElement
                          }
                          dropdownStyle={{ zIndex: 2000 }}
                          placeholder="Sélectionner une salle"
                          value={selectedClassroom?.id || undefined}
                          onChange={(classroomId) => {
                            const nextClassroom = allClassrooms.find(
                              (c: any) => c.id === classroomId,
                            );
                            setSelectedClassroom(nextClassroom);
                            setValue("id_classroom", classroomId || "", {
                              shouldValidate: true,
                            });
                          }}
                        >
                          {allClassrooms.map((classroom: any) => (
                            <Select.Option
                              key={classroom.id}
                              value={classroom.id}
                              label={classroom.label ?? ""}
                            >
                              {classroom.label}
                            </Select.Option>
                          ))}
                        </Select>
                        {errors.id_classroom && (
                          <div className="invalid-feedback d-block">
                            {errors.id_classroom.message}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-xxl-6 col-xl-6 col-md-6">
                      <div className="mb-2">
                        <label
                          className="form-label mb-1"
                          htmlFor="validationServer01"
                        >
                          Date de l'examen
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          {...register("date_exam")}
                        />
                        {errors.date_exam && (
                          <div className="invalid-feedback d-block">
                            {errors.date_exam.message}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-xxl-6 col-xl-6 col-md-6">
                      <div className="mb-2">
                        <label className="form-label mb-1">
                          Heure de l'examen
                        </label>

                        <div className="d-flex align-items-center gap-2">
                          <Select
                            showSearch
                            size="large"
                            optionFilterProp="label"
                            style={{ width: "100%" }}
                            getPopupContainer={(trigger) =>
                              trigger.parentElement as HTMLElement
                            }
                            dropdownStyle={{ zIndex: 2000 }}
                            placeholder="Début"
                            value={selectedStartHour || undefined}
                            onChange={(hour) => {
                              setSelectedStartHour(hour);
                              setValue("sessionStart", hour || "", {
                                shouldValidate: true,
                              });

                              if (
                                selectedEndHour &&
                                hour &&
                                toMinutes(selectedEndHour) <= toMinutes(hour)
                              ) {
                                setSelectedEndHour("");
                                setValue("sessionEnd", "", {
                                  shouldValidate: true,
                                });
                              }
                            }}
                          >
                            {timeOptions.map((t) => (
                              <Select.Option
                                key={`start-${t}`}
                                value={t}
                                label={t}
                              >
                                {t}
                              </Select.Option>
                            ))}
                          </Select>

                          <span className="fw-semibold text-muted px-1">à</span>

                          <Select
                            showSearch
                            size="large"
                            optionFilterProp="label"
                            style={{ width: "100%" }}
                            getPopupContainer={(trigger) =>
                              trigger.parentElement as HTMLElement
                            }
                            dropdownStyle={{ zIndex: 2000 }}
                            placeholder="Fin"
                            value={selectedEndHour || undefined}
                            onChange={(hour) => {
                              setSelectedEndHour(hour);
                              setValue("sessionEnd", hour || "", {
                                shouldValidate: true,
                              });
                            }}
                            disabled={!selectedStartHour}
                          >
                            {endTimeOptions.map((t) => (
                              <Select.Option
                                key={`end-${t}`}
                                value={t}
                                label={t}
                              >
                                {t}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>

                        {errors.sessionStart && (
                          <div className="invalid-feedback d-block">
                            {errors.sessionStart.message}
                          </div>
                        )}
                        {errors.sessionEnd && (
                          <div className="invalid-feedback d-block">
                            {errors.sessionEnd.message}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-xxl-6 col-xl-6 col-md-6">
                      <div className="form-check form-switch pt-2">
                        <input
                          {...register("practical")}
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="exam-practical-switch"
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="exam-practical-switch"
                        >
                          Évaluation pratique
                        </label>
                      </div>
                      <small className="text-muted d-block mt-1 mb-2">
                        Activez si l'évaluation comporte une partie pratique.
                      </small>
                    </div>

                    <div className="col-xxl-6 col-xl-6 col-md-6">
                      <div className="form-check form-switch pt-2">
                        <input type="hidden" {...register("type")} />
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="exam-type-switch"
                          checked={examType === "Devoir"}
                          onChange={(e) => {
                            setValue(
                              "type",
                              e.target.checked ? "Devoir" : "Examen",
                              {
                                shouldValidate: true,
                              },
                            );
                          }}
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor="exam-type-switch"
                        >
                          Devoir
                        </label>
                      </div>
                      <small className="text-muted d-block mt-1 mb-2">
                        {examType === "Devoir"
                          ? "Cette évaluation sera enregistrée comme devoir."
                          : "Cette évaluation sera enregistrée comme examen."}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="card shadow-sm border-dark rounded">
                  <div className="card-header bg-light d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">Consigne de l'examen</h5>
                  </div>
                  <div className="card-body pb-0 row">
                    <div className="row">
                      {availableConsignes.map((consigne) => (
                        <div className="col-md-6" key={consigne.id}>
                          <div className="d-flex align-items-center justify-content-between border rounded ps-2 pe-2 pt-0 pb-0 mb-2">
                            <div className="form-check me-2 flex-grow-1">
                              {editingConsigneId === consigne.id ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={editingConsigneValue}
                                  onChange={(e) =>
                                    setEditingConsigneValue(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      saveEditedConsigne();
                                    }
                                    if (e.key === "Escape") {
                                      e.preventDefault();
                                      cancelEditConsigne();
                                    }
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <>
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`consigne-${consigne.id}`}
                                    checked={selectedConsigneIds.includes(
                                      consigne.id,
                                    )}
                                    onChange={() => toggleConsigne(consigne.id)}
                                  />
                                  <label
                                    className="form-check-label fs-12"
                                    htmlFor={`consigne-${consigne.id}`}
                                  >
                                    {consigne.label}
                                  </label>
                                </>
                              )}
                            </div>

                            <div className="d-flex ">
                              {editingConsigneId === consigne.id ? (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-success"
                                    onClick={saveEditedConsigne}
                                    title="Enregistrer"
                                  >
                                    <i className="ti ti-check" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-sm"
                                    onClick={() =>
                                      startEditConsigne(consigne.id)
                                    }
                                    title="Modifier"
                                  >
                                    <i className="ti ti-edit" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm text-danger"
                                    onClick={() => deleteConsigne(consigne.id)}
                                    title="Supprimer"
                                  >
                                    <i className="ti ti-trash" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mb-2">
                      <label className="form-label mb-1">
                        Ajouter une autre consigne
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Nouvelle consigne + Entrée"
                        value={newConsigne}
                        onChange={(e) => setNewConsigne(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addConsigne();
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              resetExamForm();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  CreateExamModal,
  [buildPermission(Resource.EDTE, Action.CREATE)],
  "masquer",
);
